import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

export const DEFAULT_POPOVER_TIMEOUT_MS = 5000;

interface UsePopoverControllerProps {
  manualInputEnabled: boolean;
  countryName?: string;
}

function usePopoverController({ manualInputEnabled, countryName }: UsePopoverControllerProps) {
  const { t } = useTranslation();
  const [popoverOpen, setPopoverOpen] = useState(false);

  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const previousManualInputRef = useRef(manualInputEnabled);

  const popoverLabel = useMemo(() => {
    if (manualInputEnabled) {
      return t('searchCompany.popover.countryChanged', {
        ns: 'common',
        country: countryName,
      });
    } else {
      return t('searchCompany.popover.searchReset', { ns: 'common', country: countryName });
    }
  }, [manualInputEnabled, countryName, t]);

  const clearTimer = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const closePopover = useCallback(() => {
    clearTimer();
    setPopoverOpen(false);
  }, [clearTimer]);

  const showPopover = useCallback(() => {
    setPopoverOpen(true);

    // restart timer every time
    clearTimer();

    timeoutRef.current = setTimeout(() => {
      setPopoverOpen(false);
      timeoutRef.current = null;
    }, DEFAULT_POPOVER_TIMEOUT_MS);
  }, [clearTimer]);

  /**
   * Close immediately if manual mode changes while open
   */
  useEffect(() => {
    if (popoverOpen && previousManualInputRef.current !== manualInputEnabled) {
      closePopover();
    }

    previousManualInputRef.current = manualInputEnabled;
  }, [manualInputEnabled, popoverOpen, closePopover]);

  useEffect(() => {
    return () => {
      clearTimer();
    };
  }, [clearTimer]);

  return {
    popoverOpen,
    popoverLabel,
    showPopover,
    closePopover,
  };
}

export default usePopoverController;
